import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  Button,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';

const Cookies = () => {
  const navigate = useNavigate();
  const [cookieConsent, setCookieConsent] = useState(null);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    setCookieConsent(consent);
    
    // Charger les préférences de cookies si elles existent
    const analytics = localStorage.getItem('cookieAnalytics');
    const marketing = localStorage.getItem('cookieMarketing');
    setAnalyticsEnabled(analytics === 'true');
    setMarketingEnabled(marketing === 'true');
  }, []);

  const handleSavePreferences = () => {
    localStorage.setItem('cookieAnalytics', analyticsEnabled.toString());
    localStorage.setItem('cookieMarketing', marketingEnabled.toString());
    localStorage.setItem('cookieConsent', 'custom');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    alert('Vos préférences de cookies ont été enregistrées.');
  };

  const handleResetConsent = () => {
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookieConsentDate');
    localStorage.removeItem('cookieAnalytics');
    localStorage.removeItem('cookieMarketing');
    setCookieConsent(null);
    setAnalyticsEnabled(false);
    setMarketingEnabled(false);
    alert('Vos préférences ont été réinitialisées. Le bandeau de cookies réapparaîtra lors de votre prochaine visite.');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        variant="outlined"
        sx={{ mb: 3 }}
      >
        Retour
      </Button>

      <Paper sx={{ p: { xs: 3, md: 6 }, mb: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
          Politique des Cookies
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Stack spacing={4}>
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Qu'est-ce qu'un cookie ?
            </Typography>
            <Typography variant="body1" paragraph>
              Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, tablette ou mobile) lorsque vous visitez un site web. 
              Les cookies permettent au site de mémoriser vos actions et préférences pendant une certaine période, afin que vous n'ayez pas à les ressaisir à chaque fois que vous revenez sur le site ou naviguez d'une page à l'autre.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Comment utilisons-nous les cookies ?
            </Typography>
            <Typography variant="body1" paragraph>
              Tchamba Marketplace utilise les cookies pour :
            </Typography>
            <Typography variant="body1" component="div" sx={{ pl: 2 }}>
              <ul>
                <li><strong>Cookies essentiels :</strong> Nécessaires au fonctionnement du site (authentification, panier, etc.)</li>
                <li><strong>Cookies analytiques :</strong> Pour comprendre comment les visiteurs utilisent notre site</li>
                <li><strong>Cookies de préférences :</strong> Pour mémoriser vos choix (langue, région, etc.)</li>
                <li><strong>Cookies marketing :</strong> Pour personnaliser les publicités et mesurer leur efficacité</li>
              </ul>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Types de cookies que nous utilisons
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                1. Cookies essentiels
              </Typography>
              <Typography variant="body1" paragraph>
                Ces cookies sont indispensables au fonctionnement du site. Ils vous permettent de naviguer sur le site et d'utiliser ses fonctionnalités de base. 
                Sans ces cookies, certains services que vous avez demandés ne peuvent pas être fournis.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                2. Cookies analytiques
              </Typography>
              <Typography variant="body1" paragraph>
                Ces cookies nous aident à comprendre comment les visiteurs interagissent avec notre site en collectant et rapportant des informations de manière anonyme. 
                Cela nous permet d'améliorer le fonctionnement de notre site.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                3. Cookies de préférences
              </Typography>
              <Typography variant="body1" paragraph>
                Ces cookies permettent au site de se souvenir de vos choix (comme votre nom d'utilisateur, votre langue ou la région dans laquelle vous vous trouvez) 
                et de fournir des fonctionnalités améliorées et plus personnelles.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                4. Cookies marketing
              </Typography>
              <Typography variant="body1" paragraph>
                Ces cookies sont utilisés pour diffuser des publicités qui sont plus pertinentes pour vous et vos intérêts. 
                Ils sont également utilisés pour limiter le nombre de fois que vous voyez une publicité ainsi que pour mesurer l'efficacité des campagnes publicitaires.
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Gestion de vos préférences de cookies
            </Typography>
            <Typography variant="body1" paragraph>
              Vous pouvez contrôler et/ou supprimer les cookies comme vous le souhaitez. Vous pouvez supprimer tous les cookies déjà présents sur votre ordinateur 
              et vous pouvez configurer la plupart des navigateurs pour empêcher leur placement.
            </Typography>
            
            {cookieConsent && (
              <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Vos préférences actuelles
                </Typography>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={analyticsEnabled}
                        onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                      />
                    }
                    label="Autoriser les cookies analytiques"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={marketingEnabled}
                        onChange={(e) => setMarketingEnabled(e.target.checked)}
                      />
                    }
                    label="Autoriser les cookies marketing"
                  />
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSavePreferences}
                    >
                      Enregistrer les préférences
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleResetConsent}
                      color="error"
                    >
                      Réinitialiser
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Comment désactiver les cookies dans votre navigateur
            </Typography>
            <Typography variant="body1" paragraph>
              La plupart des navigateurs vous permettent de contrôler les cookies via leurs paramètres. Voici comment procéder pour les navigateurs les plus courants :
            </Typography>
            <Typography variant="body1" component="div" sx={{ pl: 2 }}>
              <ul>
                <li><strong>Google Chrome :</strong> Paramètres → Confidentialité et sécurité → Cookies</li>
                <li><strong>Mozilla Firefox :</strong> Options → Vie privée et sécurité → Cookies et données de sites</li>
                <li><strong>Safari :</strong> Préférences → Confidentialité → Cookies</li>
                <li><strong>Microsoft Edge :</strong> Paramètres → Cookies et autorisations de site</li>
              </ul>
            </Typography>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              <strong>Note importante :</strong> Si vous désactivez les cookies, certaines fonctionnalités du site peuvent ne pas fonctionner correctement.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Durée de conservation
            </Typography>
            <Typography variant="body1" paragraph>
              Les cookies que nous utilisons ont différentes durées de vie :
            </Typography>
            <Typography variant="body1" component="div" sx={{ pl: 2 }}>
              <ul>
                <li><strong>Cookies de session :</strong> Supprimés automatiquement à la fermeture du navigateur</li>
                <li><strong>Cookies persistants :</strong> Restent sur votre appareil pendant une période déterminée (généralement entre 30 jours et 2 ans)</li>
              </ul>
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Cookies tiers
            </Typography>
            <Typography variant="body1" paragraph>
              Certains cookies sur notre site sont placés par des services tiers. Nous n'avons pas de contrôle direct sur ces cookies. 
              Nous vous encourageons à consulter les politiques de confidentialité de ces services tiers pour plus d'informations.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Modifications de cette politique
            </Typography>
            <Typography variant="body1" paragraph>
              Nous pouvons mettre à jour cette Politique des Cookies de temps à autre. Nous vous informerons de tout changement en publiant la nouvelle politique sur cette page 
              et en mettant à jour la date de "Dernière mise à jour" en haut de cette politique.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Contact
            </Typography>
            <Typography variant="body1" paragraph>
              Pour toute question concernant notre utilisation des cookies, veuillez nous contacter :
            </Typography>
            <Typography variant="body1" paragraph sx={{ pl: 2 }}>
              <strong>Email :</strong> contact@tchambamarketplace.com<br />
              <strong>Adresse :</strong> Tchamba, Togo<br />
              <strong>Page de contact :</strong> <a href="/contact" style={{ color: 'inherit' }}>/contact</a>
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default Cookies;

